import { BehaviorSubject, Observable } from 'rxjs';
import { Injectable } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { AppCookieService } from '@ace/ace';

import { Rule } from '../models/rule';

@Injectable({ providedIn: 'root' })
export class CommonRestService {
  exceptionDetailData = new ExceptionDetailData();

  private messageSource = new BehaviorSubject<ExceptionDetailData>(
    this.exceptionDetailData
  );

  constructor(private http: HttpClient, private cookieService: AppCookieService) {}

  commonGet<T = unknown>(url: string, param?: Record<string, string | number | boolean>): Observable<T> {
    return this.http.get<T>(url, { params: toHttpParams(param) });
  }

  commonGetWithPathVariable<T = unknown>(url: string, singleVariable: string | number): Observable<T> {
    return this.http.get<T>(`${url}${singleVariable}`);
  }

  commonGetWithPathVariables<T = unknown>(
    url: string,
    variable1: string | number,
    variable2: string | number
  ): Observable<T> {
    return this.http.get<T>(`${url}${variable1}/${variable2}`);
  }

  getTotalCount<T = unknown>(url: string, clientId: number, rules: Rule[]): Observable<T> {
    return this.http.post<T>(`${url}/${clientId}`, rules);
  }

  commonPost<T = unknown>(
    url: string,
    obj: unknown,
    param?: Record<string, string | number | boolean>
  ): Observable<T> {
    return this.http.post<T>(url, obj, { params: toHttpParams(param) });
  }

  commonPostWithReqBody<T = unknown>(url: string, obj: unknown): Observable<T> {
    return this.http.post<T>(url, obj);
  }

  commonPostWitPathVariable<T = unknown>(
    url: string,
    obj: unknown,
    param?: Record<string, string | number | boolean>
  ): Observable<T> {
    return this.http.post<T>(url, obj, { params: toHttpParams(param) });
  }

  commonDelete<T = unknown>(url: string, param?: Record<string, string | number | boolean>): Observable<T> {
    return this.http.delete<T>(url, { params: toHttpParams(param) });
  }

  commonGetEnumList<T = unknown>(url: string): Observable<T> {
    return this.http.get<T>(url);
  }

  postOpenNewTab(obj: Record<string, string>, url: string): void {
    // If you ever SSR this service, guard:
    if (typeof document === 'undefined') return;

    const mapForm = document.createElement('form');
    const token = this.cookieService.getCookie('VISION-XSRF-TOKEN');

    mapForm.setAttribute('target', '_blank');
    mapForm.method = 'POST';
    mapForm.action = url;

    Object.keys(obj).forEach((param: string) => {
      const mapInput = document.createElement('input');
      mapInput.type = 'hidden';
      mapInput.name = param;
      mapInput.setAttribute('value', obj[param]);
      mapForm.appendChild(mapInput);
    });

    const mapCSRFInput = document.createElement('input');
    mapCSRFInput.type = 'hidden';
    mapCSRFInput.name = '_csrf';
    mapCSRFInput.value = token;
    mapForm.appendChild(mapCSRFInput);

    document.body.appendChild(mapForm);
    mapForm.submit();
  }

  changeMessage(message: ExceptionDetailData): void {
    this.messageSource.next(message);
  }

  userListAsExcelDownload(
    url: string,
    downloadColumns: string[],
    rules: Rule[]
  ): Observable<Blob> {
    const downloadVo = new DownloadListVo();
    downloadVo.columnList = downloadColumns;
    downloadVo.filterVos = rules;
    return this.http.post(url, downloadVo, { responseType: 'blob' });
  }
}

/** Helper: convert plain object params to HttpParams safely */
function toHttpParams(
  param?: Record<string, string | number | boolean>
): HttpParams | undefined {
  if (!param) return undefined;

  let params = new HttpParams();
  for (const [k, v] of Object.entries(param)) {
    if (v === undefined || v === null) continue;
    params = params.set(k, String(v));
  }
  return params;
}

export class ExceptionDetailData {
  clientName = '';
  carrierName = '';
  fileType = '';
  tabType = '';
  errorMesg = '';
}

export class DownloadListVo {
  columnList: string[] = [];
  filterVos: Rule[] = [];
}
